import { Paper, Stack, Group, Text, Box, Progress, SimpleGrid, Badge } from '@mantine/core'
import { IconArrowsRight, IconAlertTriangle } from '@tabler/icons-react'
import { AdaptScoreRing } from '../analysis/AdaptScoreRing'
import { SectionHeader } from '../analysis/SectionHeader'

/**
 * Team-aggregate ADAPT view + complementary strengths and gaps.
 * Tolerant to partial payloads — each section only renders if present.
 */
export function TeamGroupDynamicsView({ dynamics }) {
  if (!dynamics) return null

  const {
    aggregate_signals,
    aggregate_scores,
    dominant_archetype,
    distribution,
    complementary_strengths = [],
    gaps = [],
  } = dynamics

  return (
    <Stack gap="lg">
      {(aggregate_signals || aggregate_scores) && (
        <Paper p="lg" className="glass">
          <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" fw={600} tt="uppercase" lts={1} mb="md" ta="center">
            Team Profile
          </Text>
          {aggregate_signals ? (
            <AdaptScoreRing
              mode="counts"
              signals={aggregate_signals}
              centerLabel={dominant_archetype}
            />
          ) : (
            <AdaptScoreRing mode="scores" scores={aggregate_scores} />
          )}
        </Paper>
      )}

      {distribution?.length > 0 && (
        <Box>
          <SectionHeader>Archetype Distribution</SectionHeader>
          <Paper p="lg" className="glass">
            <Stack gap="sm">
              {distribution.map((d) => (
                <Box key={d.archetype}>
                  <Group justify="space-between" mb={4}>
                    <Text size="sm" fw={600} c="cyan.2" tt="capitalize">
                      {d.archetype}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {d.count} {d.count === 1 ? 'member' : 'members'} ({Math.round(d.percentage || 0)}%)
                    </Text>
                  </Group>
                  <Progress value={d.percentage || 0} color="cyan" size="sm" radius="xl" />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Box>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        {complementary_strengths.length > 0 && (
          <Paper p="lg" className="glass" style={{ borderColor: 'rgba(0, 229, 255, 0.15)' }}>
            <Group gap="xs" mb="xs">
              <IconArrowsRight size={16} color="var(--mantine-color-cyan-4)" />
              <Text size="xs" c="cyan.3" ff="Orbitron, sans-serif" fw={600} tt="uppercase" lts={1}>
                Complementary Strengths
              </Text>
            </Group>
            <Stack gap="xs">
              {complementary_strengths.map((s, i) => (
                <Badge key={i} color="cyan" variant="light" size="md" styles={{ label: { textTransform: 'none' } }}>
                  {s}
                </Badge>
              ))}
            </Stack>
          </Paper>
        )}

        {gaps.length > 0 && (
          <Paper p="lg" className="glass" style={{ borderColor: 'rgba(255, 200, 0, 0.15)' }}>
            <Group gap="xs" mb="xs">
              <IconAlertTriangle size={16} color="var(--mantine-color-yellow-4)" />
              <Text size="xs" c="yellow.4" ff="Orbitron, sans-serif" fw={600} tt="uppercase" lts={1}>
                Gaps to Watch
              </Text>
            </Group>
            <Stack gap="xs">
              {gaps.map((g, i) => (
                <Badge key={i} color="yellow" variant="light" size="md" styles={{ label: { textTransform: 'none' } }}>
                  {g}
                </Badge>
              ))}
            </Stack>
          </Paper>
        )}
      </SimpleGrid>
    </Stack>
  )
}
